/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StorageProvider, storageValue } from 'ngx-auth-utils';
import { BiometricService } from './biometric.service';

const TOKEN_KEY = 'teleconsulting-stp--access-token';

@Injectable({ providedIn: 'root' })
export class BiometricStorageProvider extends StorageProvider {
  // Flag to simulate failed login when biometric login failed (e.g. canceled)
  // Doesn't cancel existing credentials to allow the autologin to work if the app is reopened
  // But treats the user as not logged in until the app reopens or the user logs in manually
  private biometricInitFailed = false;

  constructor(private readonly biometricService: BiometricService) {
    super();
  }

  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const available = await this.biometricService.isAvailable();
    if (!available) {
      console.warn('Biometric not available');
      this.clearLocalKeys();
      return;
    }

    if (!this.biometricService.hasStoredSecret()) {
      console.warn('No stored secret');
      this.clearLocalKeys();
      return;
    }

    if (!this.biometricService.hasAcceptedEnrollment()) {
      console.warn('No accepted enrollment');
      this.clearLocalKeys();
      return;
    }

    try {
      const secret = await this.biometricService.loadSecret();
      localStorage.setItem(TOKEN_KEY, secret);
    } catch (err: unknown) {
      this.biometricInitFailed = true;
      console.error(
        '[BiometricStorageProvider] initialize() — failed to load secret from biometric storage:',
        err
      );
    }
  }

  async storeToken(token: string | null): Promise<void> {
    if (token == null) return;

    if (!Capacitor.isNativePlatform()) return;

    if (!this.biometricService.hasAcceptedEnrollment()) return;

    if (!this.biometricService.hasStoredSecret()) {
      await this.biometricService.registerSecret(token);
    }
    this.biometricInitFailed = false;
    localStorage.setItem(TOKEN_KEY, token);
  }

  store(key: string, value: storageValue): storageValue {
    if (this.isBiometricToken(key)) {
      if (value != null) {
        if (this.biometricService.hasStoredSecret()) {
          localStorage.setItem(TOKEN_KEY, value);
        } else {
          this.storeToken(value);
        }
      } else {
        this.biometricService.clearStoredSecretMarker();
        localStorage.removeItem(key);
      }
      return value;
    }

    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
    return value;
  }

  retrieve(key: string): storageValue {
    if (this.biometricInitFailed) return null;
    const value = localStorage.getItem(key);
    return value;
  }

  clear(key: string): void {
    if (this.biometricInitFailed) return;
    localStorage.removeItem(key);
  }

  isBiometricToken(key: string): boolean {
    return (
      key === TOKEN_KEY &&
      Capacitor.isNativePlatform() &&
      this.biometricService.hasAcceptedEnrollment()
    );
  }

  clearLocalKeys(): void {
    localStorage.removeItem(TOKEN_KEY);
  }
}
